import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { LogOut } from 'lucide-react'
import { getAccountService } from '@/pages/chat/feathers-client'
import { toast } from 'react-hot-toast'

interface AccountSectionProps {
	userId: string
	email?: string
	onLogout: () => void
}

export function AccountSection({ userId, email, onLogout }: AccountSectionProps) {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const accountService = getAccountService()

	const handleDeleteAccount = async () => {
		setIsDeleting(true)
		try {
			const result = await accountService.deleteAccount({})

			if (!result.success) {
				toast.error(result.message || 'Failed to delete account')
				setIsDeleting(false)
				return
			}

			toast.success('Account deleted successfully')
			setDeleteDialogOpen(false)

			setTimeout(() => {
				onLogout()
			}, 1000)
		} catch (error) {
			console.error('Failed to delete account:', error)
			toast.error('Failed to delete account')
			setIsDeleting(false)
		}
	}

	return (
		<>
			<div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
				<div className="w-full sm:w-32 sm:flex-shrink-0">
					<label className="block text-sm font-medium sm:text-end">UserId</label>
				</div>
				<label className="block text-sm">{userId}</label>
			</div>

			{email && (
				<div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
					<div className="w-full sm:w-32 sm:flex-shrink-0">
						<label className="block text-sm font-medium sm:text-end">Email</label>
					</div>
					<label className="block text-sm">{email}</label>
				</div>
			)}

			<div className="pt-2">
				<Button variant="outline" onClick={onLogout} className="w-full">
					<LogOut className="w-4 h-4 mr-2" />
					Logout
				</Button>
			</div>

			<div>
				<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
					<DialogTrigger asChild>
						<Button variant="outline" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50">
							Delete Account
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Delete Account</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete your account? This action cannot be undone.
								All your data will be permanently deleted, and if you have an active subscription, it will be cancelled.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setDeleteDialogOpen(false)}
								disabled={isDeleting}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={handleDeleteAccount}
								disabled={isDeleting}
							>
								{isDeleting ? 'Deleting...' : 'Delete Account'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</>
	)
}
